import { useLocale, useTranslations } from 'next-intl'

import { TNoticeSummary } from '@/entities/support'
import { UserType } from '@/entities/user'
import { Link } from '@/shared/i18n'
import { cn, useFormatDate } from '@/shared/lib'

type Props = {
  userType: UserType
  previous?: TNoticeSummary | null
  next?: TNoticeSummary | null
}

export const Navigation = ({ userType, previous, next }: Props) => {
  const { formatDate } = useFormatDate()

  const convertNoticeSummaryToNoticeItemProps = (
    noticeSummary: TNoticeSummary,
  ) => {
    return {
      href: `/${userType}/notice/${noticeSummary.id}`,
      title: noticeSummary.title,
      createdAt: formatDate({ date: noticeSummary.writeDate }),
    }
  }

  return (
    <ul className="mb-10 flex grow flex-col">
      <li className="group border-b">
        <NoticeItem
          type="previous"
          {...(previous && convertNoticeSummaryToNoticeItemProps(previous))}
        />
      </li>
      <li className="group border-b">
        <NoticeItem
          type="next"
          {...(next && convertNoticeSummaryToNoticeItemProps(next))}
        />
      </li>
    </ul>
  )
}

type NoticeItemProps = {
  type: 'previous' | 'next'
  href?: string
  title?: string
  createdAt?: string
}

const NoticeItem = ({ type, href, title, createdAt }: NoticeItemProps) => {
  const t = useTranslations('notices.detail')
  const locale = useLocale()

  const noticeType = type === 'previous' ? t('label.previous') : t('label.next')
  const noNoticeItem = t('no_post', { type: noticeType })
  const hasNoticeItem = href && title && createdAt

  if (hasNoticeItem) {
    return (
      <Link href={href} className="flex items-center py-3">
        <p
          className={cn(
            'typo-label2 mr-8.5 shrink-0 font-bold text-purple-500',
            locale === 'en' && 'w-[52px]',
          )}
        >
          {noticeType}
        </p>
        <h2 className="typo-label1-reading line-clamp-1 grow font-semibold text-gray-900">
          {title}
        </h2>
        <p className="typo-label2 ml-6 font-normal text-gray-500">
          {createdAt}
        </p>
      </Link>
    )
  }

  return (
    <div className="flex items-center py-3">
      <p
        className={cn(
          'typo-label2 mr-8.5 shrink-0 font-bold text-purple-500',
          locale === 'en' && 'w-[52px]',
        )}
      >
        {noticeType}
      </p>
      <h2 className="typo-label1-reading line-clamp-1 grow font-normal text-gray-500">
        {noNoticeItem}
      </h2>
    </div>
  )
}
